import { Box, Button, CircularProgress, Skeleton, Typography } from '@mui/material';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { Alert } from '../../../../shared/lib/common/Alert';
import {
  BUTTON_TEXT_SUBSCRIBE_PLAN,
  DATE_FORMAT,
  FILTER_STATUS_ACTIVE,
  LABEL_NEXT_BILLING_DATE,
  LABEL_PROTECTED_SINCE,
  LABEL_STATUS,
  LABEL_SUBSCRIPTION,
  LABEL_YOUR_PLAN,
} from '../../../../shared/lib/constants';
import palette from '../../../../shared/lib/theme/palette';
import { TransactionsHistoryChipStyle } from '../../../../shared/lib/theme/styleComponents';
import { billingWrapper, subscriptionPlanDetailsStyle } from '../../../../shared/lib/theme/styleConstants';
import { useAppContext } from '../../app/contexts/AppContext';
import { getInterval } from '../../app/utils';
import { Plan, useGetSubscriptionDetailLazyQuery } from '../../generated';
import { EditPlanDetails } from '../menus/EditPlanDetails';
import { useNavigate } from 'react-router-dom';
import { LINKS } from '../../app/constants';

export const PlanDetails = () => {
  const { blackTwo, blackOne, greenOne, red } = palette;
  const [currentPlan, setCurrentPlan] = useState<Plan>();
  const [currentDate, setCurrentDate] = useState<string>();
  const [status, setStatus] = useState<string | undefined>('');
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | undefined>('');
  const { currentUser } = useAppContext();
  const { isSubscribed } = currentUser ?? {};
  const { customerStripeId } = isSubscribed ?? {};
  const navigate = useNavigate();

  const [subscriptionDetail, { loading }] = useGetSubscriptionDetailLazyQuery({
    fetchPolicy: 'network-only',
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { getSubscriptionDetail } = data || {};
      if (getSubscriptionDetail) {
        const { data: getSubscriptionDetailData } = getSubscriptionDetail || {};
        const { items, status, current_period_end } = getSubscriptionDetailData[0] || {};
        const { data: itemsData } = items || {};
        const { plan, created } = itemsData.slice(-1)[0] || {};
        setCurrentPlan(plan);
        setCurrentDate(created.toString());
        setStatus(status);
        setCurrentPeriodEnd(current_period_end.toString());
      }
    }
  });

  const goToChangePlan = () => {
    navigate(LINKS.CHANGE_PLAN_LINK);
  };

  useEffect(() => {
    if (customerStripeId) {
      subscriptionDetail({
        variables: {
          customerStripeId: customerStripeId
        }
      });
    }
  }, [customerStripeId]);

  const { nickname, amount, interval } = currentPlan ?? {};
  const amountUpdate = amount ? amount / 100 : 0;
  const updateCreatedDate = currentDate && moment.tz(parseInt(currentDate || '') * 1000, 'UTC').format(DATE_FORMAT);
  const updateCurrentPeriodEnd = currentPeriodEnd && moment.tz(parseInt(currentPeriodEnd) * 1000, 'UTC').format(DATE_FORMAT);
  const isSucceeded = status === 'active';

  return (
    <Box sx={billingWrapper}>
      <Typography variant="h2" sx={{ textTransform: 'uppercase', color: blackTwo, pb: '16px' }}>
        {LABEL_YOUR_PLAN}
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          {currentPlan ? (
            <Box>
              <EditPlanDetails />

              <Typography variant="h6" sx={{ textTransform: 'uppercase', color: blackOne, pb: '24px', fontWeight: '700' }}>
                {loading ? <Skeleton width={100} /> : nickname}
              </Typography>

              <Box display="flex" mb="42px" alignItems="center">
                {loading ? (
                  <Skeleton>
                    <Typography variant="h3" sx={{ color: blackOne }}>
                      $00.00
                    </Typography>
                  </Skeleton>
                ) : (
                  <Typography variant="h3" sx={{ color: blackOne }}>
                    ${amountUpdate.toFixed(2)}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: blackTwo, ml: '10px' }}>
                  {loading ? <Skeleton /> : `/${getInterval(interval)}`}
                </Typography>
              </Box>

              <Box sx={subscriptionPlanDetailsStyle}>
                <Box>
                  <Typography variant="h2" sx={{ color: blackTwo, textTransform: 'uppercase', pb: '16px' }}>
                    {LABEL_SUBSCRIPTION}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ color: blackOne, textTransform: 'capitalize' }}>
                    {loading ? <Skeleton /> : interval}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h2" sx={{ color: blackTwo, textTransform: 'uppercase', pb: '16px' }}>
                    {LABEL_STATUS}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ color: blackOne, textTransform: 'capitalize' }}>
                    {loading ? <Skeleton /> : <TransactionsHistoryChipStyle size="small" label={isSucceeded ? FILTER_STATUS_ACTIVE : status} customcolor={isSucceeded ? greenOne : red} />}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h2" sx={{ color: blackTwo, textTransform: 'uppercase', pb: '16px' }}>
                    {LABEL_PROTECTED_SINCE}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: blackOne }}>
                    {loading ? <Skeleton /> : updateCreatedDate}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h2" sx={{ color: blackTwo, textTransform: 'uppercase', pb: '16px' }}>
                    {LABEL_NEXT_BILLING_DATE}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: blackOne }}>
                    {loading ? <Skeleton /> : updateCurrentPeriodEnd}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box display="flex" justifyContent={'center'} sx={{ my: 4 }}>
              <Button variant="contained" onClick={goToChangePlan}>
                {BUTTON_TEXT_SUBSCRIBE_PLAN}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
